import type { TokenResponse, SuggestedUrl, ApiMessage } from './types';

// Token cache
let cachedToken: TokenResponse | null = null;

/**
 * Fetch a new JWT token from the backend
 */
export async function fetchToken(apiBaseUrl: string): Promise<TokenResponse> {
  // Check if we have a valid cached token (with 30 second buffer)
  if (cachedToken && cachedToken.expiresAt > Date.now() + 30000) {
    return cachedToken;
  }
  
  const response = await fetch(`${apiBaseUrl}/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Access denied. This widget is not configured for this domain.');
    }
    throw new Error('Failed to authenticate. Please refresh the page and try again.');
  }
  
  const data: TokenResponse = await response.json();
  cachedToken = data;
  return data;
}

/**
 * Clear the cached token
 */
export function clearToken(): void {
  cachedToken = null;
}

/**
 * Send chat message and get response from the backend
 * Note: This is a non-streaming version for API Gateway compatibility
 */
export async function streamChat(
  apiBaseUrl: string,
  token: string,
  messages: ApiMessage[],
  conversationId: string,
  onFinal: (text: string) => void,
  onDisclaimer: (text: string) => void,
  onSuggestedUrls: (urls: SuggestedUrl[]) => void,
  onError: (error: string) => void,
  onDone: () => void
): Promise<void> {
  const url = `${apiBaseUrl}/chat/stream`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ messages, conversationId }),
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearToken();
        throw new Error('Session expired. Please try again.');
      }
      if (response.status === 403) {
        throw new Error('Access denied.');
      }
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Something went wrong. Please try again.');
    }
    
    const data = await response.json();

    // Show the assistant's reply
    if (data.message) {
      onFinal(data.message);
    }
    
    // Show disclaimer
    if (data.disclaimer) {
      onDisclaimer(data.disclaimer);
    }
    
    // Show suggested URLs
    if (data.suggestedUrls && data.suggestedUrls.length > 0) {
      onSuggestedUrls(data.suggestedUrls);
    }
    
    onDone();
    
  } catch (error) {
    if (error instanceof Error) {
      onError(error.message);
    } else {
      onError('An unexpected error occurred');
    }
    onDone();
  }
}
